import React from 'react';
import { ParsedUrlQuery } from 'querystring';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import without from 'lodash/without';
import styled, { css } from 'styled-components';
import {
  Box,
  FilterChipOption,
  FilterChipsDismissible,
  useMediaQuery,
} from '@sparknz/spark-pattern-library';
import { LinkRegion, trackCustomLink } from '@sparknz/tracking';

import { DeviceGalleryQuery } from '__generated__/DeviceGalleryQuery.graphql';
import { removeUnavailableFilterOptions } from 'journey/deviceGallery/utils/applyFilters.utils';
import { filterDisplayLabelMap } from 'journey/deviceGallery/utils/displayLabelMapping';
import useQuery from 'shared/hooks/useQuery';
import { convertToArray } from 'shared/utils/query.utils';
import { FilterIds } from '../../typings';
import SortFilter from './SortFilter';

const SectionContainer = styled.div`
  ${({ theme: { media, spacing } }) => css`
    width: 100%;
    padding-top: ${spacing.s2};

    ${media.up('slim')} {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    }
  `}
`;
interface CategorisFilterChipOption extends FilterChipOption {
  filterId: string;
  optionId: string;
}

type BuildFilterChipsParameter = {
  filters: DeviceGalleryQuery['response']['deviceGallery']['filters'];
  query: ParsedUrlQuery;
};

function buildFilterChips({
  filters,
  query,
}: BuildFilterChipsParameter): CategorisFilterChipOption[] {
  return Object.keys(query).flatMap((queryKey) => {
    if (queryKey === FilterIds.Sort || queryKey === FilterIds.Price) {
      return [];
    }
    const filter = filters.find(({ filterId }) => filterId === queryKey);
    if (!filter) {
      return [];
    }

    const selectedFilterValues = convertToArray(query[queryKey]);

    const displayLabelsForFilter = filterDisplayLabelMap[queryKey];

    return selectedFilterValues.map((selectedFilterValue) => {
      const matchingOption = filter.options.find(
        (option) => option.optionId === selectedFilterValue,
      );

      return {
        id: `${filter.filterId}-${selectedFilterValue}`,
        label: displayLabelsForFilter
          ? displayLabelsForFilter[matchingOption.label]
          : matchingOption.label,
        filterId: filter.filterId,
        optionId: matchingOption.optionId,
      };
    });
  });
}

interface FiltersBottomSection {
  filters: DeviceGalleryQuery['response']['deviceGallery']['filters'];
  products: DeviceGalleryQuery['response']['deviceGallery']['products'];
}

const FiltersBottomSection = ({ filters, products }: FiltersBottomSection) => {
  const isSlimView = useMediaQuery({ max: 'slim' });
  const { replaceQuery, query } = useQuery();
  const chips = buildFilterChips({ filters, query });

  const handleRemoveChip = (chip: CategorisFilterChipOption) => {
    const existingFilterValues = convertToArray(query[chip.filterId]);

    let newQuery;

    if (existingFilterValues.length === 1) {
      newQuery = { ...omit(query, chip.filterId) };
    } else {
      newQuery = {
        ...query,
        [chip.filterId]: without(existingFilterValues, chip.optionId),
      };
    }

    replaceQuery(removeUnavailableFilterOptions(filters, products, newQuery));
  };

  function handleResetClick() {
    trackCustomLink('Reset');
    replaceQuery(pick(query, ['categoryId']));
  }

  return (
    <SectionContainer title="FilterChips">
      <LinkRegion regionId="filter.tags">
        <FilterChipsDismissible
          options={chips}
          enableReset
          onReset={handleResetClick}
          onRemoveChip={handleRemoveChip}
        />
      </LinkRegion>
      {isSlimView ? (
        <Box pt="s1">
          <SortFilter />
        </Box>
      ) : null}
    </SectionContainer>
  );
};

export default FiltersBottomSection;
